import { Fragment, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, Typography, Skeleton } from "@mui/material"
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Link from '@mui/material/Link'

import { openModal } from "../../store"
import MainContainer from "../../components/Container/MainContainer"
import BlockContainer from "../../components/Container/BlockContainer"
import AppContentHeader from '../../components/AppContentHeader';
import BaseLink from "../../components/BaseLink";
import BaseImage from "../../components/BaseImage";
import BaseButton from '../../components/BaseButton';
import BaseAccordion from '../../components/BaseAccordion';
import BaseReactTooltip from '../../components/BaseReactTooltip';
import StatementSection from '../../components/StatementSection';
import RiskScoreCalculator from '../../components/RiskScoreCalculator'
import ReportReviewBottomNav from '../../components/ReportReviewBottomNav';
import GeneButtonList from '../../components/GeneButtonList';
import AlzheimerSubSectionCard from '../../components/AlzheimerSubSectionCard';
import { REDIRECT_PATH_STORAGE_ITEM } from '../../constants';
import IconBulb from "../../assets/images/icons/icon-bulb.svg"
import { setOpenChatModal } from '../../store';
import { HOW_IT_WORKS_ARTICLES, REPORTS_VIDEO_LIST } from '../../constants';
import { findMemberByUserId } from '../../utils/member-lookup';

const Report = () => {
  const { id: patient_id, report_name } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const loadingAuth = useSelector(state => state.loadingAuth)
  const reports = useSelector(state => state.patientReports)
  const reportDetail = useMemo(() => report_name ? reports.find(item => item.value === report_name) : null, [reports, report_name])
  const members = useSelector(state => state.members)
  const selectedPatient = useMemo(() => findMemberByUserId(members, patient_id), [members, patient_id])
  const [alzheimerData, setAlzheimerData] = useState()
  const selectedArticle = HOW_IT_WORKS_ARTICLES.find(item => item.label === reportDetail?.name)

  const getReportFileName = () => {
    return selectedPatient?.user?.kits?.[0]?.accessionId + '-' + report_name + '.pdf'
  }
  const reportSections = useMemo(() => {
    const data = reportDetail?.data?.report_sections || []
    
    if (data.length > 0) {
      let variantArray = []
      
      data.forEach((item => {
        if(item.alzheimer_report) {
          
          setAlzheimerData(item.alzheimer_report)
        }
        let tempVariants = []
        const subVariants = item.variants
        const snv = subVariants?.snv
        const sv = subVariants?.sv
        let label = '';
        
        if(subVariants) {
          label = item.label
        }
        if(Array.isArray(subVariants)) {
          tempVariants = [...tempVariants, ...subVariants]
        }
        else if(snv || sv) {
          if (snv?.length > 0) {
            label = item.label
            tempVariants = [...tempVariants, ...snv]
          }
          if (sv?.length > 0) {
            tempVariants = [...tempVariants, ...sv]
          }
        }
        
        variantArray.push({
          label: label,
          variants: tempVariants
        })
      }))
      return variantArray
    }
    return []
  }, [reportDetail?.data?.report_sections])

  const recommendations = useMemo(() => {
    let textFields = reportDetail?.data?.text_fields;
    if(textFields && Array.isArray(textFields)) {
      return textFields.find(rec => rec.label === 'Recommendations');
    }
    if(textFields !== null && typeof textFields === 'object') {
      return {
        body: textFields['Recommendations']
      }
    }
  }, [reportDetail])

  const isPositive = useMemo(() => reportDetail?.data?.test_result?.toLowerCase() === 'positive', [reportDetail?.data?.test_result])

  const handleClickTalkToUse = () => {
    dispatch(setOpenChatModal(true))
  }
  const openVideoModal = (e) => {
    e.stopPropagation()
    dispatch(
      openModal({
        currentModal: 'REPORT_VIDEO_SUMMARY',
        modalProps: {
          videoUrl: REPORTS_VIDEO_LIST[report_name]
        }
    }))
  }
  const handleBack = () => {
    const savedRedirectPath = decodeURIComponent(localStorage.getItem(REDIRECT_PATH_STORAGE_ITEM));
    const currentPath = decodeURIComponent(window.location.pathname);
    
    if(savedRedirectPath === currentPath) {
      localStorage.removeItem(REDIRECT_PATH_STORAGE_ITEM);
      navigate('/')
    } else {
      navigate(`/patient/${patient_id}`)
    }
  }

  return (
    <MainContainer
      sx={{
        padding: 0,
        borderRadius: 0,
        overflowX: 'hidden',
        background: "linear-gradient(180deg, #F6F9FC 0%, #C6E7FF 100%)",
      }}
    >
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box className='content-header-wave'></Box>
        <BlockContainer
          sx={{
            position: 'relative',
            padding: '16px 20px',
            zIndex: 10,
            mt: '-90px'
          }}
        >
          <AppContentHeader
            hasBackLink
            onBack={handleBack}
            backLabel=''
            pdfDownloadUrl={reportDetail?.pdfUrl}
            pdfName={getReportFileName()}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              padding: '50px 0 38px',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: '50px'
              }}
            >
              {loadingAuth ? (
                <Skeleton variant="rounded" width={210} height={30} />
              ) : (
                <Typography variant="h1">
                  {reportDetail?.name}
                </Typography>
              )}
              {reportDetail?.name === 'ACMG Actionable' &&
                <Typography variant="h4">
                  American College of Medical Genetics has selected variants with actionability
                </Typography>
              }
              <StatementSection />
            </Box>
            {loadingAuth ? (
              <Skeleton variant="rounded" width={160} height={160} />
            ) : (
              <BaseImage
                src={reportDetail?.icon}
                sx={{
                  width: '160px',
                  height: '160px',
                  position: 'absolute',
                  right: 0,
                  top: { xs: 50, md: 100 },
                  opacity: 0.4
                }}
              />
            )}
          </Box>
        </BlockContainer>
        <BlockContainer
          sx={{
            px: '20px',
            mb: '20px',
            position: 'relative',
            zIndex: 10
          }}
        >
          <Stack spacing={'32px'} direction={'column'}>
            {report_name === 'Heart' && (
              <RiskScoreCalculator />
            )}
            <Box
              sx={{
                border: '2px solid',
                borderColor: isPositive ? 'primary.red' : 'primary.darkGreen',
                borderRadius: '8px',
                padding: '16px',
                bgcolor: isPositive ? 'primary.lightRed' : 'primary.lightGreen',
                position: 'relative',
                zIndex: 10
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  zIndex: 10
                }}
              >
                <Typography variant='h2'>
                  Your test result:
                </Typography>
                <HelpOutlineIcon 
                  sx={{
                    color: 'primary.black',
                    fontSize: '24px',
                    cursor: 'pointer',
                    '&: hover': {
                      color: 'primary.mainBlue',
                    }
                  }}
                  id='test-result-help'
                />
                <BaseReactTooltip 
                  anchorSelect='#test-result-help'
                  place="bottom"
                  style={{
                    background: '#ECEFF1',
                    borderRadius: '8px',
                    color: '#1B1C1E',
                    boxShadow: '0px 2px 10px 0px #5B87EF33',
                    zIndex: '10'
                  }}
                  border="1px solid #ABB7C0"
                >
                  <Box
                    sx={{
                      maxWidth: '327px',
                      color: 'primary.darkBlack'
                    }}
                  >
                    <Box>
                      <Typography variant='subText'>
                        This does not reflect all possible factors of disease, especially environmental, and we recommend that you speak to your doctor about your results even if negative.
                      </Typography>
                    </Box>
                  </Box>
                </BaseReactTooltip>
              </Box>
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: isPositive ? 'primary.red' : 'primary.darkGreen',
                  borderRadius: '8px',
                  padding: '2px 8px',
                  boxShadow: '0px 2px 10px 0px #5B87EF33',
                  mt: '16px'
                }}
              >
                {loadingAuth ? (
                  <Skeleton variant="rounded" width={100} height={20} />
                ) : (
                  <Typography
                    variant='h3'
                    color='white'
                  >
                    {reportDetail?.data?.test_result}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  borderRadius: '8px',
                  // border: '1px solid',
                  // borderColor: 'primary.mediumGray',
                  bgcolor: 'primary.white',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  mt: '16px'
                }}
              >
                <Typography variant="body">
                  Please note:
                </Typography>
                {loadingAuth ? (
                  <Stack spacing={'4px'} direction={'column'}>
                    <Skeleton variant="rounded" width={'100%'} height={30} />
                    <Skeleton variant="rounded" width={'100%'} height={20} />
                    <Skeleton variant="rounded" width={'100%'} height={20} />
                    <Skeleton variant="rounded" width={'100%'} height={20} />
                  </Stack>
                ) : (
                  <Typography variant="body" whiteSpace={'pre-line'}>
                    {reportDetail?.data?.result_text}
                  </Typography>
                )}
                <BaseButton
                  buttonType={'default'}
                  sx={{
                    maxWidth: { md: '150px' },
                    mt: '16px'
                  }}
                  onClick={handleClickTalkToUse}
                >
                  <Typography variant="body">
                    Talk to us
                  </Typography>
                  <ArrowForwardIosIcon
                    sx={{
                      width: '14px',
                      ml: '12px'
                    }}
                  />
                </BaseButton>
                <Box
                  sx={{
                    bgcolor: 'primary.lightGray',
                    height: '1px',
                    width: '100%',
                    my: '16px'
                  }}
                >
                </Box>
                <Box>
                  <BaseAccordion
                    isExpand={false}
                    isExpandIcon
                    item={{
                      title: 'What was tested',
                    }}
                    sx={{
                      bgcolor: 'primary.lightGray',
                      boxShadow: '0px'
                    }}
                  >
                    <Box sx={{mb: '14px'}}>
                      <Link sx={{cursor: 'pointer'}} onClick={openVideoModal}>
                        <Typography variant='body'>
                          Video Summary
                        </Typography>
                      </Link>
                    </Box>
                    <Typography
                      variant="body"
                      color="primary.darkGray"
                      whiteSpace='pre-wrap'
                    >
                      {selectedArticle?.description}
                    </Typography>
                    {selectedArticle?.genes && (
                      <Typography
                        variant="body"
                        color="primary.darkGray"
                        whiteSpace='pre-wrap'
                      >
                        <GeneButtonList genes={selectedArticle.genes} styleProps={{
                          fontSize: '15px',
                          fontWeight: '500',
                        }}/>
                      </Typography>
                    )}
                  </BaseAccordion>
                </Box>
              </Box>
            </Box>
            {/* Alzheimer Section */}
            
            {alzheimerData && 
                <AlzheimerSubSectionCard data={alzheimerData} sx={{mt: 4}}/>
            }
           
            {reportSections.map((report => (
              <Stack
                key={report.label}
                spacing={'24px'}
                direction={'column'}
                sx={{
                  padding: '24px 16px',
                  borderRadius: '8px',
                  bgcolor: 'white',
                  boxShadow: '0px 2px 10px 0px #5B87EF33'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant='h4' color='primary.mainBlue'>{report.label}</Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: '8px'
                      }}
                    >
                      <Typography variant='h2' color='primary.red'>{report.variants.length}</Typography>
                      <Typography variant='h2' ml='8px'>Variants detected</Typography>
                    </Box>
                    {/* <Typography variant='body'>
                      {reportDetail?.data?.variant_summary_text}
                    </Typography> */}
                  </Box>
                  <HelpOutlineIcon
                    id='primary-finding-help'
                    sx={{
                      color: 'primary.black',
                      fontSize: '24px',
                      cursor: 'pointer',
                      '&: hover': {
                        color: 'primary.mainBlue',
                      }
                    }}
                  />
                  <BaseReactTooltip 
                    anchorSelect='#primary-finding-help'
                    place="bottom"
                    style={{
                      background: '#ECEFF1',
                      borderRadius: '8px',
                      color: '#1B1C1E',
                      boxShadow: '0px 2px 10px 0px #5B87EF33',
                      zIndex: 20
                    }}
                    border="1px solid #ABB7C0"
                  >
                    <Box
                      sx={{
                        maxWidth: '327px',
                        color: 'primary.darkBlack'
                      }}
                    >
                      <Box>
                        <Typography variant='subText'>
                          What are variants?
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: '10px'
                        }}
                      >
                        <Typography variant='subText'>
                          Variants are changes in your DNA sequence that make up a gene. Here, we specifically bring up pathogenic (or likely pathogenic) variants, which are variants is responsible for causing disease.
                        </Typography>
                      </Box>
                    </Box>
                  </BaseReactTooltip>
                </Box>
                {report.variants && report.variants.map(((variant, idx) => (
                  <Fragment key={idx}>
                    {variant.reported_transcripts && variant.reported_transcripts.map((transcript) => {
                      let svType = ''
                      if (variant?.sv_type === 'DUP') svType = 'Duplication'
                      else if (variant?.sv_type === 'DEL') svType = 'Deletion'
                      else if (variant?.sv_type === 'INS') svType = 'Insertion'

                      let secondText = ''
                      if (variant.variant_type === 'sv') secondText = svType
                      if (variant.variant_type === 'snv') secondText = variant.variant_type
                      return (
                        <BaseLink to={`/patient/${patient_id}/reports/${report_name}/${transcript.hgvs_plevel || transcript.transcript_id}`} key={transcript.hgvs_plevel || transcript.transcript_id}>
                          <Box
                            sx={{
                              padding: '16px',
                              borderRadius: '4px',
                              borderLeft: '4px solid',
                              borderColor: 'primary.mainBlue',
                              boxShadow: '0px 2px 20px 0px #5B87EF66',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              color: 'primary.black'
                            }}
                          >
                            <Typography
                              variant='h3'
                              sx={{
                                textTransform: 'uppercase'
                              }}
                            >
                              {transcript.gene_symbol} - {secondText}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Typography variant='body' sx={{ display: { xs: 'none', md: 'flex' } }}>Learn more</Typography>
                              {/* <Box
                                sx={{
                                  display: { xs: 'none', md: 'flex' },
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  bgcolor: 'primary.lightGreen',
                                  ml: '4px'
                                }}
                              >
                                <Typography variant='subText' color='primary.darkGray'>{transcript?.transcript_id}</Typography>
                              </Box> */}
                              <ChevronRightIcon
                                sx={{
                                  fontSize: '24px',
                                  ml: '8px'
                                }}
                              />
                            </Box>
                          </Box>
                        </BaseLink>
                      )
                    })}
                  </Fragment>
                )))}
              </Stack>
            )))}
            
            <Box
              sx={{
                display: 'none',
                padding: '16px',
                borderRadius: '8px',
                bgcolor: 'white',
                boxShadow: '0px 2px 10px 0px #5B87EF33'
              }}
            >
              <Typography variant='h2'>
                Cancer risk
              </Typography>
              <Typography variant='h3' mt='8px' color='primary.darkGray'>
                According to your test results, you are at the risk of following diseases:
              </Typography>
              <Box
              >
                {new Array(3).fill(null).map(((_, idx) => (
                  <Typography
                   variant='h3'
                    sx={{
                      display: 'inline-block',
                      bgcolor: 'primary.lightRed',
                      borderRadius: '8px',
                      padding: '8px',
                      mr: '16px',
                      mt: '16px'
                    }}
                    key={idx}
                  >
                      Breast cancer
                  </Typography>
                )))}
              </Box>
            </Box>
            <BaseAccordion
              isExpand
              item={{
                title: 'Recommendations',
              }}
            >
              <Stack spacing={'16px'}>
                {recommendations && recommendations.body.map(((item, idx) => (
                  <Box
                    sx={{
                      display: 'flex',
                      borderRadius: '8px',
                      padding: '16px',
                      cursor: 'pointer',
                      bgcolor: 'primary.lightGray'
                    }}
                    key={idx}
                  >
                    <Box>
                      <BaseImage
                        src={IconBulb}  
                        sx={{
                          width: '24px',
                          height: '24px'
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        ml: '8px',
                        mt: '2px'
                      }}
                    >
                      {item && Array.isArray(item) && (
                        <>
                        {item.map(((text, idx) => (
                          <Typography variant="subText" mt={'4px'} key={idx}>
                            {text}
                          </Typography>
                        )))}
                        </>
                      )}
                      {item && !Array.isArray(item) && (
                        <Typography variant="subText" mt={'4px'}>
                          {item}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )))}
              </Stack>
            </BaseAccordion>
          </Stack>
        </BlockContainer>
      </Box>
      <ReportReviewBottomNav reportDetail={reportDetail} />
    </MainContainer>
  )
}

export default Report